import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Check, Building2 } from "lucide-react";
import { api } from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";
import { SectionHeader } from "@/components/ui-kit";

const BENEFITS = ["Logo placement", "Donor wall", "Social-media recognition", "Event signage", "Employee volunteer day", "Website profile", "Speaking engagement"];

export default function Sponsors() {
  useSeo("Corporate Sponsorship", "Partner with Golden Wolf Foundation. Corporate sponsorship levels from Pack Supporter to Founding Campus Sponsor.");
  const [data, setData] = useState({ tiers: {}, sponsors: [] });
  const [f, setF] = useState({ organization: "", contact_name: "", email: "", phone: "", tier: "", message: "" });
  const [loading, setLoading] = useState(false);
  useEffect(() => { api.get("/sponsors").then((r) => setData(r.data)); }, []);

  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await api.post("/sponsors/inquiry", f); toast.success("Thank you — our partnerships team will reach out."); setF({ organization: "", contact_name: "", email: "", phone: "", tier: "", message: "" }); }
    catch { toast.error("Submission failed"); } finally { setLoading(false); }
  };

  const tiers = Object.entries(data.tiers);

  return (
    <div className="max-w-7xl mx-auto px-6 py-16">
      <SectionHeader overline="Corporate Partners" title="Sponsor the Mission" sub="Align your brand with strength, service and purpose. Benefits are customized with each partner before final agreements." center />

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-8">
        {tiers.map(([name, amount], i) => (
          <motion.div key={name} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 3) * 0.08 }}
            className={`gw-card p-7 ${i === 2 ? "border-[#D4AF37]/50" : ""}`} data-testid={`tier-${name.replace(/\s/g, "-").toLowerCase()}`}>
            <Building2 className="w-6 h-6 text-[#D4AF37]" />
            <h3 className="font-heading text-xl font-semibold text-[#FDFBF7] mt-3">{name}</h3>
            <div className="font-heading text-3xl font-bold text-[#D4AF37] mt-2">${amount.toLocaleString()}{name.includes("Founding") ? "+" : ""}</div>
            <ul className="mt-4 space-y-2">{BENEFITS.slice(0, Math.min(BENEFITS.length, 3 + i)).map((b) => (
              <li key={b} className="flex gap-2 text-sm text-[#C8C4B7]"><Check className="w-4 h-4 text-[#D4AF37] shrink-0 mt-0.5" />{b}</li>))}</ul>
          </motion.div>
        ))}
        <div className="gw-card p-7 border-dashed" data-testid="tier-custom">
          <Building2 className="w-6 h-6 text-[#4A5D23]" />
          <h3 className="font-heading text-xl font-semibold text-[#FDFBF7] mt-3">Custom In-Kind Partner</h3>
          <p className="text-sm text-[#C8C4B7] mt-3">Contribute materials, services or expertise. We'll build a partnership around what you can offer.</p>
        </div>
      </div>

      <p className="text-xs text-[#8A877B] mb-12">Golden Wolf Foundation does not promise tax deductibility beyond what is legally permitted. Sponsorship benefits are finalized in a written agreement.</p>

      <div className="grid lg:grid-cols-2 gap-12">
        <div>
          <h3 className="font-heading text-2xl text-[#FDFBF7] mb-4">Our Partners</h3>
          <div className="grid grid-cols-2 gap-4">
            {data.sponsors.map((s) => (
              <div key={s.id} className="gw-card p-5 flex items-center gap-3" data-testid={`sponsor-wall-${s.id}`}>
                <img src={s.logo} alt={s.organization} className="w-12 h-12 object-cover rounded-full" />
                <div><div className="text-[#FDFBF7] text-sm font-semibold">{s.organization}</div><div className="text-xs text-[#D4AF37]">{s.tier}</div></div>
              </div>
            ))}
          </div>
        </div>
        <form onSubmit={submit} className="gw-card p-7 space-y-4">
          <h3 className="font-heading text-2xl text-[#FDFBF7]">Become a Sponsor</h3>
          <input required placeholder="Organization *" className="gw-input" data-testid="spo-org" value={f.organization} onChange={(e) => setF({ ...f, organization: e.target.value })} />
          <input required placeholder="Contact name *" className="gw-input" data-testid="spo-name" value={f.contact_name} onChange={(e) => setF({ ...f, contact_name: e.target.value })} />
          <div className="grid grid-cols-2 gap-4">
            <input type="email" required placeholder="Email *" className="gw-input" data-testid="spo-email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
            <input placeholder="Phone" className="gw-input" data-testid="spo-phone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} />
          </div>
          <select className="gw-input" data-testid="spo-tier" value={f.tier} onChange={(e) => setF({ ...f, tier: e.target.value })}>
            <option value="">Interested level…</option>{tiers.map(([n]) => <option key={n} value={n}>{n}</option>)}<option value="Custom In-Kind Partner">Custom In-Kind Partner</option>
          </select>
          <textarea rows={3} placeholder="Tell us about your goals" className="gw-input" data-testid="spo-message" value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} />
          <button disabled={loading} className="btn-gold w-full flex items-center justify-center gap-2 py-3.5" data-testid="spo-submit">{loading && <Loader2 className="w-4 h-4 animate-spin" />} Submit Inquiry</button>
        </form>
      </div>
    </div>
  );
}
